import { Navbar, Footer } from "@/components/Layout";
import { useListingFlow } from "@/contexts/ListingFlowContext";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { filterProvinces, filterSpecies } from "@/lib/filterData";
import { useState, useMemo } from "react";

function LocationPicker({
  selectedProvince,
  selectedMunicipality,
  onSelect,
  open,
  onClose,
}: {
  selectedProvince: string;
  selectedMunicipality: string;
  onSelect: (province: string, municipality: string) => void;
  open: boolean;
  onClose: () => void;
}) {
  const [provinceSearch, setProvinceSearch] = useState("");
  const [municipalitySearch, setMunicipalitySearch] = useState("");
  const [tempProvince, setTempProvince] = useState(selectedProvince);

  const sortedProvinces = useMemo(
    () => filterProvinces.slice().sort((a, b) => a.name.localeCompare(b.name, "sv")),
    []
  );

  const filteredProvinces = sortedProvinces.filter((p) =>
    p.name.toLowerCase().includes(provinceSearch.toLowerCase())
  );

  const activeProvince = filterProvinces.find((p) => p.name === tempProvince);
  const sortedMunicipalities = activeProvince
    ? activeProvince.municipalities.slice().sort((a, b) => a.localeCompare(b, "sv"))
    : [];
  const filteredMunicipalities = sortedMunicipalities.filter((m) =>
    m.toLowerCase().includes(municipalitySearch.toLowerCase())
  );

  const handleClose = () => {
    setProvinceSearch("");
    setMunicipalitySearch("");
    setTempProvince(selectedProvince);
    onClose();
  };

  const handleProvincePick = (name: string) => {
    setTempProvince(name);
    setMunicipalitySearch("");
  };

  const handleMunicipalityPick = (name: string) => {
    onSelect(tempProvince, name);
    setProvinceSearch("");
    setMunicipalitySearch("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="flex max-h-[80vh] w-[92vw] max-w-md flex-col gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-b border-border p-4">
          <DialogTitle>{tempProvince ? `Välj kommun i ${tempProvince}` : "Välj landskap"}</DialogTitle>
        </DialogHeader>

        {!tempProvince ? (
          <>
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={provinceSearch}
                  onChange={(e) => setProvinceSearch(e.target.value)}
                  placeholder="Sök landskap..."
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {filteredProvinces.map((p) => (
                  <button
                    key={p.name}
                    onClick={() => handleProvincePick(p.name)}
                    className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2 p-4">
              <button
                onClick={() => setTempProvince("")}
                className="text-xs text-primary hover:underline"
              >
                ← Byt landskap
              </button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={municipalitySearch}
                  onChange={(e) => setMunicipalitySearch(e.target.value)}
                  placeholder="Sök kommun..."
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {filteredMunicipalities.map((m) => (
                  <button
                    key={m}
                    onClick={() => handleMunicipalityPick(m)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      selectedMunicipality === m && tempProvince === selectedProvince
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TreeInfoPopup({ listingType, data, onUpdate, open, onClose }: {
  listingType: string;
  data: { woodAge: string; diameter: string; moistureContent: string; processingGrade: string; plankLength: string; plankWidth: string; plankHeight: string };
  onUpdate: (v: any) => void;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[80vh] w-[92vw] max-w-md flex-col gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-b border-border p-4">
          <DialogTitle>Träinformation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto p-4">
          <div>
            <Label>Ålder (år)</Label>
            <Input
              type="number"
              value={data.woodAge}
              onChange={(e) => onUpdate({ woodAge: e.target.value })}
              placeholder="T.ex. 30"
            />
          </div>

          {listingType === "trad" && (
            <div>
              <Label>Genomsnittlig diameter (cm)</Label>
              <Input
                value={data.diameter}
                onChange={(e) => onUpdate({ diameter: e.target.value })}
                placeholder="T.ex. 25"
              />
            </div>
          )}

          {(listingType === "plankor" || listingType === "ved") && (
            <div>
              <Label>Fuktkvot</Label>
              <Select value={data.moistureContent} onValueChange={(v) => onUpdate({ moistureContent: v })}>
                <SelectTrigger><SelectValue placeholder="Välj fuktkvot" /></SelectTrigger>
                <SelectContent className="z-[1100]">
                  <SelectItem value="< 16-18%">{"< 16-18%"}</SelectItem>
                  <SelectItem value="> 20%">{"> 20%"}</SelectItem>
                  <SelectItem value="> 24-30%">{"> 24-30%"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {listingType === "plankor" && (
            <div>
              <Label>Dimensioner (cm)</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  value={data.plankLength}
                  onChange={(e) => onUpdate({ plankLength: e.target.value })}
                  placeholder="Längd"
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  value={data.plankWidth}
                  onChange={(e) => onUpdate({ plankWidth: e.target.value })}
                  placeholder="Bredd"
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  value={data.plankHeight}
                  onChange={(e) => onUpdate({ plankHeight: e.target.value })}
                  placeholder="Höjd"
                />
              </div>
            </div>
          )}

        </div>
        <div className="border-t border-border p-4">
          <Button className="w-full" onClick={onClose}>Spara</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CreateListingStep3() {
  const { data, updateData } = useListingFlow();
  const navigate = useNavigate();
  const [locationOpen, setLocationOpen] = useState(false);
  const [treeInfoOpen, setTreeInfoOpen] = useState(false);

  if (!data.category) {
    navigate("/skapa-annons", { replace: true });
    return null;
  }

  const treeInfoComplete = (() => {
    if (!data.woodAge) return false;
    if (data.listingType === "trad" && !data.diameter) return false;
    if ((data.listingType === "plankor" || data.listingType === "ved") && !data.moistureContent) return false;
    return true;
  })();

  const locationComplete = data.province !== "" && data.municipality !== "";
  const canContinue = data.species !== "" && locationComplete && treeInfoComplete;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <div className="container max-w-lg flex-1 py-6">
        <BackButton fallback="/skapa-annons/detaljer" />

        <h1 className="mb-6 font-display text-2xl font-bold text-foreground">Platsinfo & träinformation</h1>

        {/* Trädslag */}
        <div className="mb-6">
          <Label>Trädslag</Label>
          <Select value={data.species} onValueChange={(v) => updateData({ species: v })}>
            <SelectTrigger><SelectValue placeholder="Välj trädslag" /></SelectTrigger>
            <SelectContent>
              {filterSpecies.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Plats */}
        <div className="mb-4">
          <button
            onClick={() => setLocationOpen(true)}
            className="flex w-full items-center justify-between rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary"
          >
            <div>
              <p className="font-semibold text-foreground">Plats</p>
              <p className="text-sm text-muted-foreground">
                {locationComplete
                  ? `${data.municipality}, ${data.province}`
                  : "Välj landskap och kommun"}
              </p>
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Plus className="h-4 w-4" />
            </div>
          </button>
        </div>

        {/* Träinformation */}
        <div className="mb-6">
          <button
            onClick={() => setTreeInfoOpen(true)}
            className="flex w-full items-center justify-between rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:border-primary"
          >
            <div>
              <p className="font-semibold text-foreground">Träinformation</p>
              <p className="text-sm text-muted-foreground">
                {treeInfoComplete ? "Ifylld ✓" : "Välj ålder, kvalitet, mm"}
              </p>
            </div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Plus className="h-4 w-4" />
            </div>
          </button>
        </div>

        <Button className="w-full" disabled={!canContinue} onClick={() => navigate("/skapa-annons/pris")}>
          Fortsätt
        </Button>
      </div>
      <Footer hideOnMobile />

      <LocationPicker
        selectedProvince={data.province}
        selectedMunicipality={data.municipality}
        onSelect={(province, municipality) => updateData({ province, municipality })}
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
      />
      <TreeInfoPopup
        listingType={data.listingType ?? "trad"}
        data={data}
        onUpdate={updateData}
        open={treeInfoOpen}
        onClose={() => setTreeInfoOpen(false)}
      />
    </div>
  );
}
